import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { PortableText } from '@portabletext/react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/opacity.css';
import SubscriptionForm from "./SubscriptionForm";
import { fetchRecentPosts, fetchSinglePost } from "../../../lib/fetchBlogs";
import { urlFor } from "../../../util/util";

export default function SinglePost() {
  const [blog, setBlog] = useState(null); // State for single post
  const [recentBlogs, setRecentBlogs] = useState([]); // State for recent posts
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const { slug } = useParams(); // Get slug from URL

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  function formatDate(date) {
    const options = { day: "numeric", month: "long", year: "numeric" };
    return new Date(date).toLocaleDateString("en-GB", options);
  }

  // Fetch data when slug changes
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [post, recent] = await Promise.all([
          fetchSinglePost(slug),
          fetchRecentPosts(),
        ]);
        setBlog(post || null);
        setRecentBlogs(recent || []);
      } catch (error) {
        console.error("Error fetching blog data:", error);
        setBlog(null);
        setRecentBlogs([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (slug) fetchData();
  }, [slug]);

  // Render loading state
  if (isLoading) {
    return <h1 className="loading-blog-details-text">Loading...</h1>;
  }

  // Render "Post not found" if no blog data
  if (!blog) {
    return <h1 className="not-found-text">Post not found</h1>;
  }

  return (
    <div className="single-blog">
      {/* Post header */}
      <div className="container">
        <section className="single-post-header">
          <div className="post-header">
            <h1 className="post-title">
              {blog.title}
            </h1>
          
            {blog.mainImage?.asset?.url && (
              <img
                src={blog.mainImage.asset.url}
                alt={blog.title}
                title={blog.title}
              />
            )}
            
          </div>
        </section>

        {/* Content grid */}
        <div className="container content-grid">
          {/* Left section */}
          <div className="left-section sticky top-6">
            {/* Author Info Card */}
            <div className="author-info bg-white p-4 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200">
              <div className="mb-4">
                <h4 className="!text-xs !font-medium !text-gray-500 mb-1">AUTHOR</h4>
                <div className="mt-1">
                  <Link 
                    to={`/author/${blog.author?.slug?.current || ''}`}
                    className="flex items-center group"
                  >
                    {blog.author?.image?.asset?.url && (
                      <div className="flex-shrink-0 mr-3">
                        <img
                          src={urlFor(blog.author.image).width(80).height(80).url()}
                          alt={blog.author.name}
                          className="w-10 h-10 rounded-full object-cover border-2 border-blue-100 group-hover:border-blue-200 transition-colors duration-200"
                        />
                      </div>
                    )}
                    <div className="leading-tight">
                      <div className="text-xs font-normal text-gray-500 group-hover:text-blue-600 transition-colors">
                        {blog.author?.name || 'Unknown Author'}
                      </div>
                      {blog.author?.jobTitle && (
                        <div className="text-[11px] text-gray-500 mt-0.5">
                          {blog.author.jobTitle}
                        </div>
                      )}
                    </div>
                  </Link>
                </div>
              </div>
            </div>
            
            {/* Publish Date & Categories */}
            <div className="mt-4 bg-white p-4 rounded-lg shadow-sm border border-gray-100">
              <div className="mb-4">
                <h4 className="text-xs font-medium text-gray-500 mb-1">PUBLISHED</h4>
                <time dateTime={blog.publishedAt} className="text-sm font-medium text-gray-900">
                  {formatDate(blog.publishedAt)}
                </time>
              </div>
              
              {blog.categories && blog.categories.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 mb-2">CATEGORIES</h4>
                  <div className="flex flex-wrap gap-2">
                    {blog.categories.map((category) => (
                      <Link 
                        key={category._id}
                        to={`/blog?category=${encodeURIComponent(category.title)}`}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors duration-200"
                      >
                        {category.title}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Main content */}
          <div className="block-content">
            <PortableText
              value={blog.body}
              components={{
                // Custom component for gallery images
                types: {
                  galleryImage: ({ value }) => (
                    <div className="gallery-image-container my-8">
                      <div className="relative overflow-hidden rounded-lg">
                        <LazyLoadImage
                          src={urlFor(value.image).width(1200).quality(80).url()}
                          alt={value.alt || ''}
                          effect="opacity"
                          className="w-full h-auto rounded-lg transition-all duration-300 hover:scale-105"
                        />
                        {value.caption && (
                          <p className="!mt-0.5 !text-xs !text-gray-500 !text-left !italic" style={{ marginTop: '-5px !important' }}>
                            {value.caption}
                          </p>
                        )}
                      </div>
                    </div>
                  ),
                  // Handle regular images
                  image: ({ value }) => (
                    <div className="my-8">
                      <LazyLoadImage
                        src={urlFor(value).width(1200).quality(80).url()}
                        alt={value.alt || 'Blog image'}
                        effect="opacity"
                        className="w-full h-auto rounded-lg"
                      />
                      {value.caption && (
                        <p className="!mt-0.5 !text-xs !text-gray-500 !text-center !italic" style={{ marginTop: '2px !important' }}>
                          {value.caption}
                        </p>
                      )}
                    </div>
                  ),
                },
                // Text formatting
                block: {
                  h1: ({ children }) => <h1 className="text-3xl font-bold my-6">{children}</h1>,
                  h2: ({ children }) => <h2 className="text-2xl font-bold my-5">{children}</h2>,
                  h3: ({ children }) => <h3 className="text-xl font-semibold my-4">{children}</h3>,
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-blue-500 pl-4 italic my-4 text-gray-600">
                      {children}
                    </blockquote>
                  ),
                  normal: ({ children }) => (
                    <p className="my-4 leading-relaxed text-gray-700">
                      {children}
                    </p>
                  ),
                  list: ({ children }) => (
                    <ul className="list-disc pl-5 my-4 space-y-2">{children}</ul>
                  ),
                  listItem: ({ children }) => (
                    <li className="text-base leading-6 text-gray-700">{children}</li>
                  ),
                },
                // Marks for text formatting
                marks: {
                  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                  em: ({ children }) => <em className="italic">{children}</em>,
                  code: ({ children }) => (
                    <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">
                      {children}
                    </code>
                  ),
                  link: ({ value, children }) => {
                    const target = value?.blank ? '_blank' : undefined;
                    return (
                      <a
                        href={value?.href}
                        target={target}
                        rel={target === '_blank' ? 'noopener noreferrer' : ''}
                        className="text-blue-600 hover:underline"
                      >
                        {children}
                      </a>
                    );
                  },
                },
              }}
            />
          </div>

          {/* Recent posts */}
          <div className="recent-blog-section">
            <h1 className="recent-blog-title">
              Recent Blog
            </h1>
            <div className="recent-posts">
              {
                recentBlogs.map((post) => (
                  <div
                    key={post.slug?.current || post._id}
                    className="recent-post">
                    <Link
                      to={`/blog/${post.slug?.current}`}
                      className="recent-post-link">
                      {post.mainImage?.asset?.url ? (
                        <img
                          className="recent-post-img"
                          src={post.mainImage.asset.url}
                          alt={post.mainImage.alt || post.title || 'Blog image'}
                        />
                      ) : (
                        <div className="recent-post-img recent-post-img-placeholder" aria-hidden="true" />
                      )}
                      <p className="recent-post-title">
                        {post.title}
                      </p>
                    </Link>
                  </div>
                ))
              }
            </div>
          </div>
        </div>

        {/* Subscription form */}
        <SubscriptionForm />
      </div>
    </div>
  );
}
